import React, { CSSProperties, useState, useEffect } from 'react';
import { Form } from 'antd';
import { Rule } from 'rc-field-form/lib/interface';
import { CustomTagProps } from 'rc-select/lib/BaseSelect';
import useFormValidation from '../../utils/useFormValidation';
import { Validations } from '../../interfaces';
import { useIntl } from 'react-intl';
import { AntdSelect } from './style';
import { FormItemLayout } from 'antd/es/form/Form';
import { Icon } from '../Icon';
import { BaseOptionType, DefaultOptionType } from 'antd/es/select';
import { useBreakpoint } from 'utils/useBreakpoint';

export interface SelectOptionsValueStringNumber {
  label: string;
  value: number;
}
export interface SelectOptionsValueString {
  label: string;
  value: string;
}

export interface LabelValueType {
  key?: string;
  label: React.ReactNode;
  value: string | number;
}

export interface SelectProps extends Validations {
  placeholder?: React.ReactNode;
  children?: React.ReactNode;
  defaultValue?:
  | string
  | number
  | (string | number)[]
  | LabelValueType
  | LabelValueType[];
  style?: React.CSSProperties;
  onChange?: (
    value: any,
    option?:
      | DefaultOptionType
      | BaseOptionType
      | (DefaultOptionType | BaseOptionType)[]
  ) => void;
  size?: 'large' | 'middle' | 'small';
  mode?: 'multiple' | 'tags';
  value?: number | string | string[] | LabelValueType | LabelValueType[];
  showSearch?: boolean;
  maxTagCount?: number | 'responsive';
  allowClear?: boolean;
  loading?: boolean;
  optionLabelProp?: string;
  label?: string;
  name?: (string | number)[] | string | number;
  dropdownRender?: (menu: React.ReactElement) => React.ReactElement;
  tagRender?: (props: CustomTagProps) => React.ReactElement;
  rules?: Rule[];
  initialValue?:
  | string
  | string[]
  | number
  | number[]
  | LabelValueType
  | LabelValueType[];
  placement?: 'bottomLeft' | 'bottomRight' | 'topLeft' | 'topRight';
  noStyle?: boolean;
  disabled?: boolean;
  className?: string;
  variant?: 'outlined' | 'borderless' | 'filled';
  suffixIcon?: React.ReactNode;
  onDropdownVisibleChange?: (open: boolean) => void;
  dropdownStyle?: CSSProperties;
  popupClassName?: string;
  getPopupContainer?: (triggerNode: HTMLElement) => HTMLElement;
  virtual?: boolean;
  required?: boolean;
  layout?: FormItemLayout;
  labelInValue?: boolean;
  freezeScreenOnOpen?: boolean;
  onSelect?: (value: any, option: DefaultOptionType | BaseOptionType) => void;
  onClear?: () => void;
}

const { Option } = AntdSelect;

export const SelectOption = Option;

const getSearchableText = (value: unknown): string => {
  if (value == null) {
    return '';
  }

  if (typeof value === 'string' || typeof value === 'number') {
    return String(value).toLowerCase();
  }

  if (Array.isArray(value)) {
    return value
      .map((item) => getSearchableText(item))
      .filter(Boolean)
      .join(' ');
  }

  return '';
};

export const Select = ({
  disabled,
  placeholder = 'Izvēlēties',
  className,
  children,
  onChange,
  defaultValue,
  style,
  size = 'large',
  value,
  mode,
  showSearch = true,
  maxTagCount,
  allowClear,
  loading,
  optionLabelProp,
  label,
  name,
  dropdownRender,
  tagRender,
  initialValue,
  placement,
  labelInValue,
  noStyle,
  validations,
  variant,
  suffixIcon,
  onDropdownVisibleChange,
  dropdownStyle,
  popupClassName,
  getPopupContainer = (triggerNode: HTMLElement) => triggerNode,
  virtual,
  required,
  layout,
  rules = [],
  freezeScreenOnOpen = false,
  onSelect,
  onClear,
}: SelectProps) => {
  const { formValidations } = useFormValidation();
  const intl = useIntl();
  const combinedRules = validations ? formValidations(validations) : rules;
  const { isLgDown } = useBreakpoint();

  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!freezeScreenOnOpen) return;

    if (open) {
      document.body.classList.add('screen-freeze');
      document.addEventListener('touchmove', blockTouchMove, {
        passive: false,
      });
    } else {
      document.body.classList.remove('screen-freeze');
      document.removeEventListener('touchmove', blockTouchMove);
    }

    return () => {
      document.body.classList.remove('screen-freeze');
      document.removeEventListener('touchmove', blockTouchMove);
    };
  }, [open]);

  const blockTouchMove = (e: TouchEvent) => {
    const dropdown = document.querySelector('.ant-select-dropdown');
    if (dropdown && dropdown.contains(e.target as Node)) {
      return; // allow touch inside dropdown
    }
    e.preventDefault(); // block everywhere else
  };

  const handleDropdownVisibleChange = (visible: boolean) => {
    setOpen(visible);
    onDropdownVisibleChange?.(visible);
  };

  return (
    <Form.Item
      label={label}
      name={name}
      initialValue={initialValue}
      noStyle={noStyle}
      rules={combinedRules}
      layout={layout}
      required={required}
    >
      <AntdSelect
        disabled={disabled}
        dropdownStyle={dropdownStyle}
        popupClassName={popupClassName}
        virtual={virtual}
        onDropdownVisibleChange={handleDropdownVisibleChange}
        placeholder={placeholder}
        className={className}
        getPopupContainer={getPopupContainer}
        onChange={(value, option) =>
          onChange?.(value, option as DefaultOptionType | BaseOptionType)
        }
        onClear={onClear}
        defaultValue={defaultValue}
        style={style}
        size={size}
        value={value}
        mode={mode}
        suffixIcon={
          isLgDown ? <Icon name={'arrow-mobile'} /> : <Icon name={'arrow'} />
        }
        showSearch={showSearch}
        maxTagCount={maxTagCount}
        labelInValue={labelInValue}
        allowClear={allowClear}
        variant={variant}
        filterOption={(input: string, option: any) => {
          if (mode === 'tags') {
            return true;
          }
          const query = String(input ?? '').toLowerCase();
          const searchableText = getSearchableText(
            option?.label ?? option?.children
          );
          return searchableText.includes(query);
        }}
        loading={loading}
        optionLabelProp={optionLabelProp}
        dropdownRender={dropdownRender}
        tagRender={tagRender}
        placement={placement}
        notFoundContent={intl.formatMessage({ id: 'general.select_no_data' })}
        onSelect={(val, option) => {
          onSelect?.(val, option);
          // @ts-ignore to not render caret after select choice is made
          document?.activeElement?.blur();
        }}
      >
        {children}
      </AntdSelect>
    </Form.Item>
  );
};
